import { useIntl } from 'react-intl';
import { GetStaticProps } from 'next';

import { CarouselSection } from '@components/CarouselSection';
import { ProductFeature } from '@components/ProductFeature';
import { RecipeListCard } from '@components/RecipeListCard';
import { RecipeListingHighlightSection } from '@components/RecipeListingHighlightSection';

import { getRecipeHomePage } from '@data/sanity/queries';
import { recipeMessages } from '@data/ui-messages/recipes';
import { CmsRecipeHomePageDocument } from '@interfaces/common';
import { withSiteWideData } from '@utils/page';

interface RecipeIndexPageProps {
	recipeHomePage: CmsRecipeHomePageDocument;
}

const RecipesPage: React.VFC<RecipeIndexPageProps> = ({
	recipeHomePage: {
		header: { image, title },
		highlightContent,
		recipesByProduct,
	},
}) => {
	const intl = useIntl();

	return (
		<>
			<ProductFeature
				headingLevel={1}
				imageUrl={image}
				padding="xxl"
				statement={title}
			/>

			<RecipeListingHighlightSection {...highlightContent} />

			{recipesByProduct.map(({ metadata, product, productRecipes }) => (
				<CarouselSection
					backgroundColor={product.category.highlightColor}
					carousel={productRecipes}
					description={metadata.description}
					heading={intl.formatMessage(recipeMessages.productRecipes, {
						product: product.metadata.title,
					})}
					key={metadata.slug}
					link={{
						text: intl.formatMessage(recipeMessages.allProductRecipes, {
							product: product.metadata.title,
						}),
						url: `/recipes/product/${metadata.slug}`,
					}}
					renderItem={RecipeListCard}
					textColor={{ hex: '#000', alpha: 1 }}
				/>
			))}
		</>
	);
};

export const getStaticProps: GetStaticProps =
	withSiteWideData<RecipeIndexPageProps>(async ({ locale }) => {
		const recipeHomePage = await getRecipeHomePage(locale);

		if (!recipeHomePage) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				metadata: recipeHomePage.metadata || null,
				recipeHomePage,
				newsletterTheme: 'light',
			},
		};
	});

export default RecipesPage;
